// ** React Imports
import { useState, useEffect } from "react";

// ** MUI Imports
import {
  Box,
  Button,
  Grid,
  Backdrop,
  CircularProgress,
  Card,
  CardHeader,
  CardContent,
  Typography,
  Divider,
  FormControlLabel,
  InputAdornment,
  IconButton,
  Checkbox,
  Avatar,
  Popover,
  Badge,
  Chip,
} from "@mui/material";
import Tab from "@mui/material/Tab";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import TabContext from "@mui/lab/TabContext";
import { useTheme, styled } from "@mui/material/styles";
import { DataGrid } from "@mui/x-data-grid";
import { DataGridPro } from '@mui/x-data-grid-pro';
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers-pro/LocalizationProvider";
import { DatePicker } from '@mui/x-date-pickers-pro';

import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import useMediaQuery from "@mui/material/useMediaQuery";

// ** Icon Imports
import Icon from "components/icon";
import AddIcon from "@mui/icons-material/Add";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import DashboardIcon from '@mui/icons-material/GridViewOutlined';
import ManageAccountsIcon from  '@mui/icons-material/ManageAccountsOutlined';
import DownloadIcon from '@mui/icons-material/SystemUpdateAltOutlined';
import ListIcon  from '@mui/icons-material/ChecklistOutlined';
import ClearIcon from "@mui/icons-material/Clear";
import CustomChip from "components/mui/chip";

// ** Custom Component Imports
import CustomTextField from "../../components/mui/text-field";

// ** Component Imports
import MonthlySubscriptionDialog from "./component/MonthlySubscriptionDialog";
import AddEmployeeDialog from "./component/AddEmployee";
import EditClockInOutDialog from "./component/EditClockInOut";
import AddToDoDialog from "./component/AddToDo";
import AssignTaskDialog from "./component/AssignTask";
import Calendar from "./component/calendar";
import DashboardTour from "./component/DashboardTour";
import AddEmployeeDoodle from "images/AddEmployeeDoodle.png";
import AccessTimeIcon from '@mui/icons-material/AccessTime'; 

// ** Logic Imports
import { useIndexLogic } from "./logic/index-logic";
import { getUser } from "utils/auth";
import { GetFormatedDateStamp } from "components/formatDate";
import { useDashboardPermission } from "./permissions/DashboardPermissions";
import { formatUSCurrency } from "components/CurrencyFormat";

// ** Third-Party Imports
import dayjs from "dayjs";

// Layout Imports
import UserLayout from "components/layouts/UserLayout";

//#region Styled Components
const ToDoComponent = ({ serialNo, text, checked, handleTodoCheck, todo }) => {
  return (
   <Box
  sx={{
    display: "flex",
    ml: 3,
    alignItems: "center",
    pb: 2,
    borderBottom: "1px solid #E1E1E1",
  }}
>
  <Box>
    <FormControlLabel
      control={
        <Checkbox
          checked={checked}
          onChange={(e) => {
            handleTodoCheck(todo, e.target.checked);
          }}
          color="success"
        />
      }
    />
  </Box>
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 2,
    }}
  >
    <Typography>{serialNo}</Typography>
    <Typography 
      color="secondary"
      sx={{
        textDecoration: checked ? 'line-through' : 'none'
      }}
    >
      {text}
    </Typography>
  </Box>
</Box>
  );
};

const BadgeContentSpan = styled("span")(({ theme }) => ({
  width: 8,
  height: 8,
  borderRadius: "50%",
  backgroundColor: theme.palette.success.main,
  boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
}));

const dropdownItemsStyle = {
  px: 6,
  py: 2,
  "&:hover": {
    backgroundColor: "action.hover",
    cursor: "pointer",
  },
};

const PopoverButton = ({ taskFilterType, setTaskFilterType }) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleFilterSelect = (type) => {
    setTaskFilterType(type);
    handleClose();
  };

  const getInitials = (name) => {
    if (!name) return "";
    const words = name.split(" ");
    return words
      .map((word) => word[0])
      .join("")
      .toUpperCase();
  };
 
  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  // For bold or checkmark on the current filter (optional)
  const getMenuStyle = (type) =>
    taskFilterType === type
      ? { ...dropdownItemsStyle, fontWeight: 700, color: "#23a964" }
      : dropdownItemsStyle;

      const filterLabelMap = {
  MY_OPENED: "My Open Tasks",
  ALL_TASKS: "All Tasks",
  ALL_OPENED: "All Opened Tasks",
  ALL_CLOSED: "All Closed Tasks",
  MY_CLOSED: "My Closed Tasks",
};


  return (
    <div>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          cursor: "pointer",
        }}
        onClick={handleClick}
      >
        <Typography variant="h3">Tasks</Typography>
        <Icon icon="tabler:chevron-down" />
          <CustomChip
      label={filterLabelMap[taskFilterType]}
      color="info"
      skin="light"
      size="small"
      sx={{ fontWeight: 500 }}
    />
      </Box>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Box sx={{ py: 2 }}>
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography
              variant="h6"
              sx={getMenuStyle("MY_OPENED")}
              onClick={() => handleFilterSelect("MY_OPENED")}
            >
              My Open Tasks
            </Typography>
            <Typography
              variant="h6"
              sx={getMenuStyle("ALL_TASKS")}
              onClick={() => handleFilterSelect("ALL_TASKS")}
            >
              All Tasks
            </Typography>
            <Typography
              variant="h6"
              sx={getMenuStyle("ALL_OPENED")}
              onClick={() => handleFilterSelect("ALL_OPENED")}
            >
              All Opened Tasks
            </Typography>
            <Typography
              variant="h6"
              sx={getMenuStyle("ALL_CLOSED")}
              onClick={() => handleFilterSelect("ALL_CLOSED")}
            >
              All Closed Tasks
            </Typography>
            <Typography
              variant="h6"
              sx={getMenuStyle("MY_CLOSED")}
              onClick={() => handleFilterSelect("MY_CLOSED")}
            >
              My Closed Tasks
            </Typography>
          </Box>
        </Box>
      </Popover>
    </div>
  );
};
//#endregion Styled Components

const Dashboard = () => {
  //#region Hooks
  const theme = useTheme();
  const smAbove = useMediaQuery(theme.breakpoints.up("sm"));
  const smBelow = useMediaQuery(theme.breakpoints.down("sm"));
  //#endregion Hooks

  //#region Props Destructuring
  const { IndexProps } = useIndexLogic();
  const {
    contentHeightFixed,
    collapse,
    setCollapse,
    loading,
    tabValue,
    tabTitle,
    handleTabChange,
    tabMain,
    handleTabMainChange,
    checkIn,
    handleCheckIn,
    openAssignTaskDlg,
    handleOpenAssignTaskDlg,
    handleCloseAssignTaskDlg,
    columnsAssignedTasks,
    // dummyRowsAssignedTasks,
    tabAssignTaskValue,
    handleAssignTaskTabChange,
    activeButton,
    handleFilterClick,
    openToDoDlg,
    handleOpenToDoDlg,
    handleCloseToDoDlg,
    openAddEmpDlg,
    handleOpenAddEmpDlg,
    handleCloseAddEmpDlg,
    columnsEmployee,
    filteredRows,
    filteredtaskRows,
    openEditClockInOutDlg,
    handleCloseEditClockInOutDlg,
    handleClearSearch,
    handleSearchChange,
    searchText,
    getYesterdayTotalMs,
    formatElapsed,
    selectedEmployee,
    todos,
    setTodos,
    fetchTodos,
    fetchLogs,
    handleTodoCheck,
    setActiveButton,
    paginationModel,
    handlePaginationModel,
    filteredTodos,
    totalDisplay,
     yesterdayDisplay,
    currentDisplay,
    dateDisplay,
    rows,
    rowsAssignedTasks,
    fetchAndJoin,
    taskFilterType,
    setTaskFilterType,
    filteredAssignedTasks,
    handleDashboardTourClose,
    isTourOpen,
    setIsTourOpen,
     onBreak,
  handleBreakToggle,
  breakElapsed,
  formatTime,
    onboardingStatus,
    load,
    navigate,
    editingLog,
    logsToday,
    scoreCardData
  } = IndexProps;
  //#endregion Props Destructuring

  //#region Permission Context
    const { DashboardPermissionProps } = useDashboardPermission();
    const {
      // Shift Management
      hasShiftGetPermission,
      hasShiftSavePermission,
      hasShiftDeletePermission,
  
      // Shift With Break
      hasShiftWithBreakGetPermission,
  
      // Breaks
      hasBreakGetPermission,
      hasBreakSavePermission,
      hasBreakDeletePermission,
  
      // To-Do
      hasToDoGetPermission,
      hasToDoSavePermission,
      hasToDoDeletePermission,
  
      // Tour
      hasTourGetPermission,
      hasTourSavePermission
    } = DashboardPermissionProps;
    //#endregion Permission Context

  //#region States
  const [showWelcomePopup, setShowWelcomePopup] = useState(true);
  const [showTrialPopup, setShowTrialPopup] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  
  //const [User, setUser] = useState(() => getUser());
  //#endregion States

  //#region Methods
  const getInitials = (name) => {
    if (!name) return "";
    const words = name.split(" ");
    return words
      .map((word) => word[0])
      .join("")
      .toUpperCase();
  };
const User = getUser();
  const getDaysCount = (endDate) => {
    const currentDate = dayjs();
    const end = dayjs(endDate);
    return end.diff(currentDate, "day");
  };

  // useEffect(() => {
  //   const handleStorageChange = () => {
  //     const updatedUser = getUser();
  //     setUser(updatedUser);
  //   };

  //   // Listen to storage changes (when localStorage changes across tabs)
  //   window.addEventListener("storage", handleStorageChange);

  //   // Optional: listen to custom events for same-tab updates
  //   window.addEventListener("user-updated", handleStorageChange);

  //   return () => {
  //     window.removeEventListener("storage", handleStorageChange);
  //     window.removeEventListener("user-updated", handleStorageChange);
  //   };
  // }, []); 
  //#endregion Methods

  useEffect(()=> {
    load();
  },[])

  const todoTabOptions = [
    { label: "All", value: "All" },
    { label: "Upcoming", value: "Upcoming" },
    { label: "Completed", value: "Completed" },
    { label: "Overdue", value: "Overdue" }
  ];

  useEffect(() => {
    if(smBelow){
      setCollapse(true)
    }
  }, [])
  
  const subscriptionStatus = onboardingStatus && onboardingStatus.clientOnBoarding && onboardingStatus.clientOnBoarding?.subscriptionStatus.toLowerCase();

  return (
    <UserLayout
      contentHeightFixed={contentHeightFixed}
      activeLink={"Dashboard"}
    >
        {subscriptionStatus === 'paused' && showTrialPopup && (
          <Box
            sx={{
              mb: 0,
              ml: 1,
              transform: 'translateY(-12px)',
              p: 3,
            background:
              "linear-gradient(90deg, #D09A0E  20%, #775C2B 100%)",
              borderRadius: 1,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              position: "relative",
              overflow: "hidden",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            }}
          >
            <Box sx={{ pr: 4, color: "white" }}>
              <Typography
                variant="h5"
                sx={{
                  color: theme.palette.mode === 'dark' ? 'rgba(208, 212, 241, 0.68)' : "white",
                  fontWeight: 350,
                  mb: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 2.5,
                }}
              >
                <Icon
                  icon="material-symbols:info-outline"
                  width={18}
                  height={18}
                  sx={{
                    mr: 6,
                    color: theme.palette.mode === 'dark' ? 'rgba(208, 212, 241, 0.68)' : "white",
                    opacity: 0.9,
                  }}
                />
                    Limited Access 
                    <Chip label="Subscription Paused" size="small" sx={{
                      display: 'flex',
                      padding: '1.923px 7.693px 1.923px 5.77px',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: '5.77px',
                      backgroundColor: '#FFFFFF',
                    }} /> 
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 260,
                  color: "white",
                  opacity: 1.0,
                  pl: "26px", // Add padding to match the icon width + margin
                }}
              >   
              Your subscription is currently paused. Some features may be limited. Reactivate your subscription to restore full access.
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                position: "relative",
                paddingTop: "8px",
              }}
            >
              
            
              <Button
                variant="contained"
                onClick={() => navigate('/settings#5')}
                color="main"
                size="small"
                sx={{
                  whiteSpace: "nowrap",
                  backgroundColor: "white",
                  color: "primary.main",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.9)",
                  },
                }}
              >
                Manage Subscription
              </Button>
              <IconButton
                size="small"
                sx={{
                  color: "white",
                  padding: "4px",
                  "& svg": {
                    width: "18px",
                    height: "18px",
                  },
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.2)",
                  },
                }}
                onClick={() => setShowTrialPopup(false)}
              >
                <Icon icon="tabler:x" />
              </IconButton>
            </Box>
          </Box>
        )} 
        { subscriptionStatus === 'expired' && showTrialPopup && (
          <Box
            sx={{
              mb: 0,
              ml: 1,
              transform: 'translateY(-12px)',
              p: 3,
            background:
              "linear-gradient(90deg, #D80027  20%, #720015 100%)",
              borderRadius: 1,
              display: "flex",
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: "space-between",
              alignItems: "center",
              position: "relative",
              overflow: "hidden",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            }}
          >
            <Box sx={{ pr: 4, color: "white" }}>
              <Typography
                variant="h5"
                sx={{
                  color: "white",
                  fontWeight: 350,
                  mb: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 2.5,
                }}
              >
                <Icon
                  icon="material-symbols:info-outline"
                  width={18}
                  height={18}
                  sx={{
                    mr: 6,
                    color: "white",
                    opacity: 0.9,
                  }}
                />
                    Unpaid Subscription 
                    <Chip label="Subscription inactive" size="small" sx={{
                      display: 'flex',
                      padding: '1.923px 7.693px 1.923px 5.77px',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: '5.77px',
                      backgroundColor: '#FFFFFF',
                    }} /> 
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 260,
                  color: "white",
                  opacity: 1.0,
                  pl: "26px", // Add padding to match the icon width + margin
                }}
              >   
                Your payment has expired, and your subscription is inactive. Please update your payment details to restore full access.
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                position: "relative",
                paddingTop: "8px",
              }}
            >
              
            
              <Button
                variant="contained"
                onClick={() => setDialogOpen(true)}
                color="main"
                size="small"
                sx={{
                  whiteSpace: "nowrap",
                  backgroundColor: "white",
                  color: "primary.main",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.9)",
                  },
                }}
              >
                Update Payment
              </Button>
              <IconButton
                size="small"
                sx={{
                  color: "white",
                  padding: "4px",
                  "& svg": {
                    width: "18px",
                    height: "18px",
                  },
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.2)",
                  },
                }}
                onClick={() => setShowTrialPopup(false)}
              >
                <Icon icon="tabler:x" />
              </IconButton>
            </Box>
          </Box>
        )}
        {subscriptionStatus === 'trial' && showTrialPopup && (
          <Box
            sx={{
              mb: 0,
              ml: { xs: 0, md: 1 },
              transform: 'translateY(-12px)',
              p: { xs: 2, md: 3 },
              background: "linear-gradient(90deg,#003A70 0%,#007FD6 100%)",
              borderRadius: 1,
              display: "flex",
              flexDirection: { xs: 'column', md: 'row' },
              justifyContent: "space-between",
              alignItems: { xs: 'flex-start', md: 'center' },
              position: "relative",
              overflow: "hidden",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              gap: { xs: 2, md: 0 }
            }}
          >
            <Box sx={{ pr: { xs: 0, md: 4 }, color: "white" }}>
              <Typography
                variant="h5"
                sx={{
                  color: "white",
                  fontWeight: 350,
                  mb: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 2.5,
                  fontSize: { xs: '1rem', md: '1.25rem' }
                }}
              >
                <Icon
                  icon="material-symbols:info-outline"
                  width={18}
                  height={18}
                  sx={{
                    mr: { xs: 2, md: 6 },
                    color: "white",
                    opacity: 0.9,
                  }}
                />
                14-Day Free Trial
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 260,
                  color: "white",
                  opacity: 1.0,
                  pl: { xs: '26px', md: "26px" },
                  fontSize: { xs: '0.875rem', md: '1rem' }
                }}
              >              
                {getDaysCount(onboardingStatus?.clientOnBoarding?.subscriptionEndDate)} days remaining • Enter payment information to continue using
                Cleri at the end of your trial
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                position: "relative",
                mb: { xs: 0, md: 1 },
                paddingTop: { xs: 0, md: "5px" },
                paddingLeft: { xs: '26px', md: "8px" },
                alignSelf: { xs: 'flex-end', md: 'center' }
              }}
            >
              <Button
                variant="contained"
                onClick={() => setDialogOpen(true)}
                color="main"
                size="small"
                sx={{
                  whiteSpace: "nowrap",
                  backgroundColor: "white",
                  ml: { xs: 0, md: 5 },
                  mr: { xs: 0, md: 5 },
                  color: "primary.main",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.9)",
                  },
                  fontSize: { xs: '0.75rem', md: '0.875rem' }
                }}
              >
                Purchase Now
              </Button>
              <IconButton
                size="small"
                sx={{
                  color: "white",
                  padding: "4px",
                  "& svg": {
                    width: "18px",
                    height: "18px",
                  },
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.2)",
                  },
                }}
                onClick={() => setShowTrialPopup(false)}
              >
                <Icon icon="tabler:x" />
              </IconButton>
            </Box>
          </Box>
        )}
        {subscriptionStatus === 'active' && showTrialPopup && (
          <Box
            sx={{
              mt: -3,
              // mb: 0,
              // ml: { xs: 0, md: 1 },
              // transform: 'translateY(-12px)',
              p: { xs: 2, md: 3 },
              background: "linear-gradient(90deg, #23A26D 20%, #003A70 100%)",
              borderRadius: 1,
              display: "flex",
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: "space-between",
              alignItems: "center",
              // position: "relative",
              overflow: "hidden",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              gap: { xs: 2, md: 0 }
            }}
          >
            <Box sx={{ pr: { xs: 0, md: 4 }, color: "white" }}>
              <Typography
                variant="h5"
                sx={{
                  color: "white",
                  fontWeight: 350,
                  mb: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 2.5,
                  fontSize: { xs: '1rem', md: '1.25rem' }
                }}
              >
                <Icon
                  icon="material-symbols:info-outline"
                  width={18}
                  height={18}
                  sx={{
                    mr: { xs: 2, md: 6 },
                    color: "white",
                    opacity: 0.9,
                  }}
                />
                Thanks for being a member of the Cleri community!
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 260,
                  color: "white",
                  opacity: 1.0,
                  pl: { xs: '26px', md: "26px" },
                  fontSize: { xs: '0.875rem', md: '1rem' }
                }}
              >
                Your account is currently Active - Next Payment Due : {GetFormatedDateStamp(onboardingStatus?.clientOnBoarding?.subscriptionEndDate)}
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                width: {xs: '100%', sm: 'max-content'},
                gap: 2,
                // position: "relative",
                paddingTop: { xs: 0, md: "8px" },
                // alignSelf: { xs: 'flex-end', sm: 'center' },
                justifyContent: { xs: 'space-between', sm: 'flex-end' }
              }}
            >
              <Button
                variant="contained"
                onClick={() => navigate('/settings#5')}
                color="main"
                size="small"
                sx={{
                  whiteSpace: "nowrap",
                  backgroundColor: "white",
                  color: "primary.main",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.9)",
                  },
                  fontSize: { xs: '0.75rem', md: '0.875rem' }
                }}
              >
                Manage Subscription
              </Button>
              <IconButton
                size="small"
                sx={{
                  color: "white",
                  padding: "4px",
                  "& svg": {
                    width: "18px",
                    height: "18px",
                  },
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.2)",
                  },
                }}
                onClick={() => setShowTrialPopup(false)}
              >
                <Icon icon="tabler:x" />
              </IconButton>
            </Box>
          </Box>
        )}
      <Box sx={{ display: "flex", flexDirection: { xs: 'column', md: 'row' }, gap: 2, mt: 2, height: '100%', minHeight: '600px' }}>
        {/* Tab List Card - Separate card on the left */}
        <Card
        sx={{
          width: { xs: '100%', md: collapse ? 60 : 190 },
          maxWidth: { xs: '100%', md: collapse ? 60 : 200 },
          transition: "all 0.3s ease",
          overflow: "visible",
          display: { xs: 'none', md: 'block' },
          position: "relative", 
        }}
      >
        <CardContent sx={{ p: 0 }}>
          <TabContext value={tabValue}>
            <TabList
              orientation="vertical"
              onChange={handleTabChange}
              sx={{
                minWidth: collapse ? 0 : { xs: 130, md: "max-content" },
                transition: "all 0.3s ease",
                pt:5,
                gap: 4,
                  borderRight: 'none', 
              '& .MuiTabs-indicator': {
                display: 'none',
              },
              }}
            >
              <Tab
                value="1"
                label={
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      width: "100%",
                      ml: { xs: -1, md: -2 },
                      color:
                        theme.palette.mode === 'dark'
                          ? tabValue === "1"
                            ? "#fff"
                            : "inherit"
                          : "inherit",
                    }}
                  >
                    <DashboardIcon fontSize="small" />
                    <Box
                      sx={{
                        fontSize: { xs: '0.875rem', md: "1rem" },
                        fontWeight: 500,
                        whiteSpace: "nowrap",
                        opacity: collapse ? 0 : 1, // hide when collapsed
                        transition: "opacity 0.3s ease",
                      }}
                    >
                      Dashboard
                    </Box>
                  </Box>
                }
                sx={{
                  alignItems: "flex-start",
                  py: { xs: 1, md: 1.5 },
                  minHeight: 'auto',
                  justifyContent: 'flex-start',
                }}
              />

              <Tab
                value="2"
                label={
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      width: "100%",
                      ml: { xs: -1, md: -2 },
                      color:
                        theme.palette.mode === 'dark'
                          ? tabValue === "2"
                            ? "#fff"
                            : "inherit"
                          : "inherit",
                    }}
                  >
                    <ManageAccountsIcon fontSize="small" />
                    <Box
                      sx={{
                        fontSize: { xs: '0.875rem', md: "1rem" },
                        fontWeight: 500,
                        whiteSpace: "nowrap",
                        opacity: collapse ? 0 : 1,
                        transition: "opacity 0.3s ease",
                      }}
                    >
                      Manage Shifts
                    </Box>
                  </Box>
                }
                sx={{
                  alignItems: "flex-start",
                  py: { xs: 1, md: 1.5 },
                  minHeight: 'auto',
                }}
              />

              <Tab
                value="3"
                label={
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      width: "100%",
                      ml: { xs: -1, md: -2 },
                      color:
                        theme.palette.mode === 'dark'
                          ? tabValue === "3"
                            ? "#fff"
                            : "inherit"
                          : "inherit",
                    }}
                  >
                    <DownloadIcon fontSize="small" />
                    <Box
                      sx={{
                        fontSize: { xs: '0.875rem', md: "1rem" },
                        fontWeight: 500,
                        whiteSpace: "nowrap",
                        opacity: collapse ? 0 : 1,
                        transition: "opacity 0.3s ease",
                      }}
                    >
                      Task List
                    </Box>
                  </Box>
                }
                sx={{
                  alignItems: "flex-start",
                  py: { xs: 1, md: 1.5 },
                  minHeight: 'auto',
                }}
              />

              <Tab
                value="4"
                label={
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      width: "100%",
                      ml: { xs: -1, md: -2 },
                      color:
                        theme.palette.mode === 'dark'
                          ? tabValue === "4"
                            ? "#fff"
                            : "inherit"
                          : "inherit",
                    }}
                  >
                    <ListIcon fontSize="small" />
                    <Box
                      sx={{
                        fontSize: { xs: '0.875rem', md: "1rem" },
                        fontWeight: 500,
                        whiteSpace: "nowrap",
                        opacity: collapse ? 0 : 1,
                        transition: "opacity 0.3s ease",
                      }}
                    >
                      To-Do List
                    </Box>
                  </Box>
                }
                sx={{
                  alignItems: "flex-start",
                  py: { xs: 1, md: 1.5 },
                  minHeight: 'auto',
                }}
              />
            </TabList>
          </TabContext>

          {/* Collapse Button */}
          <IconButton
            sx={{
              position: "absolute",
              top: "4%",
              right: -7, 
              zIndex: 20,
              transform: "translateY(-50%)",
              backgroundColor: "#a3e2b9",
              border: `1px solid ${theme.palette.divider}`,
              width: "25px",
              height: "25px",
              color: "primary.main",
              transition: "background-color 0.2s ease-in-out",
              "&:hover": {
                backgroundColor: "#8ddca9",
              },
            }}
            size="small"
            onClick={() => setCollapse(!collapse)}
          >
            <Icon
              icon={collapse ? "tabler:chevron-right" : "tabler:chevron-left"}
              style={{ transition: "transform 0.2s ease-in-out" }}
            />
          </IconButton>
        </CardContent>
        </Card>
        {/* Mobile Tab List */}
        <Card sx={{ display: { xs: 'block', md: 'none' }, mb: 2 }}>
          <CardContent sx={{ p: 0 }}>
            <TabContext value={tabValue}>
              <TabList
                value={tabValue}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  minHeight: 'auto',
                  '& .MuiTab-root': {
                    minHeight: 'auto',
                    py: 2.5,
                    px: 2,
                    fontSize: '0.875rem'
                  }
                }}
              >
                <Tab
                  value="1"
                  label={
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 3.5,
                        width: "100%",
                        ml: { xs: -1, md: -2 },
                        color: theme.palette.mode === 'dark' ? 
                          tabValue === "1" ? "#fff" : "inherit" :
                          "inherit"
                      }}
                    >
                      <DashboardIcon fontSize="small" />
                      <Box
                        sx={{
                          fontSize: { xs: '0.875rem', md: "1rem" },
                          fontWeight: 500,
                          whiteSpace: "nowrap",
                        }}
                      >
                        Main
                      </Box>
                    </Box>
                  }
                  sx={{ 
                    alignItems: "flex-start", 
                    py: { xs: 1, md: 1.5 },
                  }}
                />
                <Tab
                  value="2"
                  label={
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 3.5,
                        width: "100%",
                        ml: { xs: -1, md: -2 },
                        color: theme.palette.mode === 'dark' ? 
                          tabValue === "2" ? "#fff" : "inherit" :
                          "inherit"
                      }}
                    >
                      <ManageAccountsIcon fontSize="small" />
                      <Box
                        sx={{
                          fontSize: { xs: '0.875rem', md: "1rem" },
                          fontWeight: 500,
                          whiteSpace: "nowrap",
                        }}
                      >
                        Manage Shifts
                      </Box>
                    </Box>
                  }
                  sx={{ alignItems: "flex-start", py: { xs: 1, md: 1.5 } }}
                />
                <Tab
                  value="3"
                  label={
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 3.5,
                        width: "100%",
                        ml: { xs: -1, md: -2 },
                        color: theme.palette.mode === 'dark' ? 
                          tabValue === "3" ? "#fff" : "inherit" :
                          "inherit"
                      }}
                    >
                      <DownloadIcon fontSize="small" />
                      <Box
                        sx={{
                          fontSize: { xs: '0.875rem', md: "1rem" },
                          fontWeight: 500,
                          whiteSpace: "nowrap",
                        }}
                      >
                        Task Management
                      </Box>
                    </Box>
                  }
                  sx={{ alignItems: "flex-start", py: { xs: 1, md: 1.5 } }}
                />
                <Tab
                  value="4"
                  label={
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2.5,
                        width: "100%",
                        ml: { xs: -1, md: -2 },
                        color: theme.palette.mode === 'dark' ? 
                          tabValue === "4" ? "#fff" : "inherit" :
                          "inherit"
                      }}
                    >
                      <ListIcon fontSize="small" />
                      <Box
                        sx={{
                          fontSize: { xs: '0.875rem', md: "1rem" },
                          fontWeight: 500,
                          whiteSpace: "nowrap",
                        }}
                      >
                        To-Do List
                      </Box>
                    </Box>
                  }
                  sx={{ alignItems: "flex-start", py: { xs: 1, md: 1.5 } }}
                />
              </TabList>
            </TabContext>
          </CardContent>
        </Card>
        <Card sx={{ 
          height: "100%", 
          width: "100%", 
          overflowX: 'auto',
          flex: 1 
        }}>
          <CardContent sx={{ height: "100%", minWidth: { xs: '320px', sm: '100%' } }}>
            <TabContext value={tabValue}>
              <Box sx={{ height: "100%" }}>
                {/* Tab Panel 1 - Main Dashboard */}
                <TabPanel value="1" sx={{    width: "100%", height: "100%", p: 0 }}>
              <Grid container spacing={0} sx={{ height: "100%" }}>   
                  <Grid item xs={12}>
              <Box sx={{ 
                p: 3, 
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                mb: 0
              }}>
              
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  mb: 5
                }}>
          
                <Box>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: "bold" }}
                  >
                    Welcome back,{" "}
                     {onboardingStatus?.fullName
                      ? onboardingStatus.fullName
                      : User?.fullName
                      ? User.fullName
                      : User?.firstName && User?.lastName
                      ? `${User.firstName} ${User.lastName}`
                      : "Guest"}
                  </Typography>
                </Box>

        
              <Box sx={{ display: "flex", gap: 2 ,mr:2}}>
              <div data-tour="time-clock-section">
                <Button
                  variant="contained"
                  color={checkIn ? (onBreak ? "primary" : "error") : "main"}
                  disabled={!hasShiftSavePermission}
                  {...(hasShiftSavePermission && { onClick: handleCheckIn })}
                  size="medium"
                  sx={{ minWidth: "140px", px: 3 }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2.5 }}>
                    <Icon icon="tabler:stopwatch" />
                    <Box sx={{ textAlign: "left" }}>
                      <Typography variant="body2" color="white" sx={{ whiteSpace: "nowrap", lineHeight: 1 }}>
                        {!checkIn ? "Punch In" : onBreak ? "Paused" : " Punch Out"}
                      </Typography>
                      <Typography variant="caption" color="white" sx={{ opacity: 0.9, lineHeight: 1 ,mt:1}}>
                        {currentDisplay}
                      </Typography>
                    </Box>
                  </Box>
                </Button>
              </div>              
              {checkIn && (
                <Button
                  variant="outlined"
                  color={onBreak ? "error" : "main"}
                  onClick={handleBreakToggle}
                  size="medium"
                  sx={{
                    minWidth: "140px",
                    px: 3,
                    borderWidth: 2,
                    "&:hover": { borderWidth: 2 }
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2.5 }}>
                    <Icon icon="mdi:coffee" />
                    <Box sx={{ textAlign: "left" }}>
                      <Typography variant="body2" color="inherit" sx={{ whiteSpace: "nowrap", lineHeight: 1 }}>
                        {onBreak ? "End Break" : "Start Break"}
                      </Typography>
                      <Typography variant="caption" color="inherit" sx={{ opacity: 0.9, lineHeight: 1 ,mt:1}}>
                        {onBreak ? formatTime(breakElapsed) : "00:00:00"}
                      </Typography>
                    </Box>
                  </Box>
                </Button>
              )}
              </Box>
            </Box>
                  {/* 4 Statistic Boxes Grid */}
                    <Grid container spacing={2} >
                      <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ p: 3, position: 'relative' }}>
                    {/* Icon positioned at top left */}
                    <Box
                      sx={{
                        display: "inline-flex",
                        bgcolor: "rgba(35, 162, 109, 0.12)",
                        borderRadius: "50%",
                        width: 34,
                        height: 34,
                        alignItems: "center",
                        justifyContent: "center",
                        position: 'absolute',
                        left: 16,
                        top: 16
                      }}
                    >
                      <Icon icon="ph:users" width={20} height={20} style={{ color: "#23A26D" }} />
                    </Box>

                    <Box sx={{ textAlign: 'right', width: '100%' }}>
                      <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                        {scoreCardData?.totalCustomers || 0}
                      </Typography>
                      <Typography variant="body1" color="text.secondary">
                        Total Customers
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 2.5, ml: 2, justifyContent: 'flex-start' }}>
                        <Icon 
                          icon={`iconoir:arrow-${scoreCardData?.customerTrend > 0 ? 'up-right' : 'down-left'}`} 
                          width={14} 
                          height={14} 
                          style={{ 
                            color: scoreCardData?.customerTrend > 0 ? '#23A26D' : 'red', 
                            marginRight: 4 
                          }} 
                        />
                        {/* <Typography variant="body2" sx={{ fontWeight: 500, mr: 1, color: '#7C8DB5' }}>
                          10.2
                        </Typography> */}
                        <Typography variant="body2" sx={{ color: '#7C8DB5' }}>
                          {scoreCardData?.customerTrend}% this week
                        </Typography>
                      </Box>
                    </Box>
                  </Card>
                </Grid>

                {/* Today's Profit */}
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ p: 3, position: 'relative' }}>
                    {/* Icon positioned at top left */}
                    <Box
                      sx={{
                        display: "inline-flex",
                        bgcolor: "rgba(35, 162, 109, 0.12)",
                        borderRadius: "50%",
                        width: 34,
                        height: 34,
                        alignItems: "center",
                        justifyContent: "center",
                        position: 'absolute',
                        left: 16,
                        top: 16
                      }}
                    >
                      <Icon icon="solar:bag-outline" width={20} height={20} style={{ color: "#23A26D" }} />
                    </Box>

                    {/* Content on right - unchanged */}
                    <Box sx={{ textAlign: 'right', width: '100%' }}>
                      <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                        {formatUSCurrency(scoreCardData?.todayEstimatedProfit || 0)}
                      </Typography>
                      <Typography variant="body1" color="text.secondary">
                        Today's Estimated Profit
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 2.5, ml: 2, justifyContent: 'flex-start' }}>
                        <Icon 
                          icon={`iconoir:arrow-${scoreCardData?.profitTrend > 0 ? 'up-right' : 'down-left'}`} 
                          width={14} 
                          height={14} 
                          style={{ 
                            color: scoreCardData?.profitTrend > 0 ? '#23A26D' : 'red', 
                            marginRight: 4 
                          }} 
                        />
                        {/* <Typography variant="body2" sx={{ fontWeight: 500, mr: 1, color: '#7C8DB5' }}>
                          10.2
                        </Typography> */}
                        <Typography variant="body2" sx={{ color: '#7C8DB5' }}>
                          {scoreCardData?.profitTrend}% this week
                        </Typography>
                      </Box>
                    </Box>
                  </Card>
                </Grid>

                {/* Invoices Due */}
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ p: 3, position: 'relative' }}>
                    {/* Icon positioned at top left */}
                    <Box
                      sx={{
                        display: "inline-flex",
                        bgcolor: "rgba(35, 162, 109, 0.12)",
                        borderRadius: "50%",
                        width: 34,
                        height: 34,
                        alignItems: "center",
                        justifyContent: "center",
                        position: 'absolute',
                        left: 16,
                        top: 16
                      }}
                    >
                      <Icon icon="hugeicons:money-bag-02" width={20} height={20} style={{ color: "#23A26D" }} />
                    </Box>

                    {/* Content on right - unchanged */}
                    <Box sx={{ textAlign: 'right', width: '100%' }}>
                      <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                        {formatUSCurrency(scoreCardData?.totalInvoiceAmount || 0)}
                      </Typography>
                      <Typography variant="body1" color="text.secondary">
                        Invoices Due
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 2.5, ml: 2, justifyContent: 'flex-start' }}>
                        <Icon 
                          icon={`iconoir:arrow-${scoreCardData?.invoiceTrend > 0 ? 'up-right' : 'down-left'}`} 
                          width={14} 
                          height={14} 
                          style={{ 
                            color: scoreCardData?.invoiceTrend > 0 ? '#23A26D' : 'red', 
                            marginRight: 4 
                          }} 
                        />
                        {/* <Typography variant="body2" sx={{ fontWeight: 500, mr: 1, color: '#7C8DB5' }}>
                          2.56
                        </Typography> */}
                        <Typography variant="body2" sx={{ color: '#7C8DB5' }}>
                          {scoreCardData?.invoiceTrend}% this week
                        </Typography>
                      </Box>
                    </Box>
                  </Card>
                </Grid>

                {/* Average Job Cost */}
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ p: 3, position: 'relative' }}>
                    {/* Icon positioned at top left */}
                    <Box
                      sx={{
                        display: "inline-flex",
                        bgcolor: "rgba(35, 162, 109, 0.12)",
                        borderRadius: "50%",
                        width: 34,
                        height: 34,
                        alignItems: "center",
                        justifyContent: "center",
                        position: 'absolute',
                        left: 16,
                        top: 16
                      }}
                    >
                      <Icon icon="solar:wallet-money-linear" width={20} height={20} style={{ color: "#23A26D" }} />
                    </Box>

                    {/* Content on right - unchanged */}
                    <Box sx={{ textAlign: 'right', width: '100%' }}>
                      <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                        {formatUSCurrency(scoreCardData?.todayEstimatedCost || 0)}
                      </Typography>
                      <Typography variant="body1" color="text.secondary">
                        Today's Estimated Cost
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 2.5, ml: 2, justifyContent: 'flex-start' }}>
                        <Icon 
                          icon={`iconoir:arrow-${scoreCardData?.costTrend > 0 ? 'up-right' : 'down-left'}`} 
                          width={14} 
                          height={14} 
                          style={{ 
                            color: scoreCardData?.costTrend > 0 ? '#23A26D' : 'red', 
                            marginRight: 4 
                          }} 
                        />
                        {/* <Typography variant="body2" sx={{ fontWeight: 500, mr: 1, color: '#7C8DB5' }}>
                          7.2
                        </Typography> */}
                        <Typography variant="body2" sx={{ color: '#7C8DB5' }}>
                          {scoreCardData?.costTrend}% this week
                        </Typography>
                      </Box>
                    </Box>
                  </Card>
                </Grid>
                    </Grid>
                      </Box>
                  </Grid>
                  {/* DataGrid Section */}
                  <Grid item xs={12}>
                  <Box sx={{ 
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    overflow: 'hidden',
                  }}>
                    <Card
                      id="tasks-section"
                      sx={{ 
                        height: "100%", 
                        maxHeight: { xs: 'none', md: "60vh" },
                        overflow: 'hidden',
                        boxShadow: 'none',
                        border: 'none'
                      }}
                    >
                      <CardHeader
                        title={
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: { xs: 2, md: 5 },
                              flexWrap: 'wrap'
                            }}
                          >
                      {/* Tab Buttons */}
                      <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 2, md: 5 }, flexWrap: 'wrap' }}>
                    <div data-tour="dashboard-tasks-section">
                      <Badge 
                        badgeContent={filteredAssignedTasks.length} 
                        color="error" 
                        invisible={tabMain !== "1" && filteredAssignedTasks.length === 0} 
                        sx={{
                          "& .MuiBadge-badge": {
                            right: -1,  
                            top: 4,     
                          }
                        }}
                      >
                        <Button
                          variant={tabMain === "1" ? "tonal" : "text"}
                          color={theme.palette.mode === 'dark' ? "secondary" : "primary"}
                          onClick={() => handleTabMainChange("1")}
                          size="small"
                        >
                          My Tasks
                        </Button>
                      </Badge>
                    </div>

                    <div data-tour="dashboard-todo-section">
                      <Button
                        variant={tabMain === "2" ? "tonal" : "text"}
                        color={theme.palette.mode === 'dark' ? "secondary" : "primary"}
                        onClick={() => handleTabMainChange("2")}
                        size="small"
                      >
                        My To-Do List
                      </Button>
                    </div>
                  </Box>

                      {/* Action Buttons */}
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: 'wrap' }}>
                        {tabMain === "1" && (
                          <Button
                            variant="contained"
                            color="main"
                            startIcon={<AddIcon />}
                            onClick={handleOpenAssignTaskDlg}
                            sx={{
                              height: 40,
                              minWidth: 150,
                              whiteSpace: "nowrap",
                            }}
                          >
                            Assign Task
                          </Button>
                        )}
                        {tabMain === "2" && (
                          <Button
                            variant="contained"
                            color="main"
                            startIcon={<AddIcon />}
                            disabled={!hasToDoSavePermission}
                            {...(hasToDoSavePermission && {
                              onClick: handleOpenToDoDlg
                            })}
                            sx={{ 
                              height: 40,
                              minWidth: 150,
                              whiteSpace: "nowrap"
                            }}
                          >
                            Add To-Do
                          </Button>
                        )}
                      </Box>
                    </Box>
                  }
                  sx={{ py: { xs: 2, md: 3 } }}
                />
                <Divider />
                <CardContent sx={{ height: "350px", p: { xs: 1, md: 2 } }}>
                  {tabMain === "1" ? (
                    <Box sx={{ height: "100%" }} data-tour="tasks-list">
                      <DataGridPro
                        key={collapse ? "collapsed" : "expanded"}
                        // pagination
                        // pageSizeOptions={[
                        //   10,
                        //   50,
                        //   { value: -1, label: "Show All" },
                        // ]}
                        // paginationModel={paginationModel}
                        // onPaginationModelChange={handlePaginationModel}
                        rows={filteredAssignedTasks}
                        columns={columnsAssignedTasks}
                        disableRowSelectionOnClick
                        slots={{
                          // noRowsOverlay: NoRowsOverlay,
                          footer: () => null
                        }}
                        sx={{
                          "& .MuiDataGrid-columnHeaderTitle": {
                            textTransform: "none",
                            fontSize: { xs: '0.75rem', md: '0.875rem' }
                          },
                          "& .MuiDataGrid-cell": {
                            fontSize: { xs: '0.75rem', md: '0.875rem' }
                          },
                          maxHeight: { xs: '400px', md: 350 },
                        }}
                      />
                    </Box>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                    height: "100%",
                    overflowY: "auto",
                  }}
                  data-tour="todo-list"
                >
                  {todos &&
                    todos.length > 0 &&
                    todos.map((todo) => {
                      return (
                        <ToDoComponent
                          key={todo?.toDoListId}
                          text={todo.name}
                          checked={
                            todo.status === "Active" ? true : false
                          }
                          handleTodoCheck={handleTodoCheck}
                          todo={todo}
                        />
                      );
                    })}
                </Box>
              )}
            </CardContent>
              </Card>
            </Box>
          </Grid>
          </Grid>
                </TabPanel>
        <TabPanel value="2" sx={{ width: "100%", p: { xs: 0, md: 2 }, pt: { xs: 2, md: 0 } }}>
        <Grid container spacing={{ xs: 2, md: 5 }} sx={{ height: "100%" }}>
    {/* Today's Shift Section */}
    <Grid item xs={12} md={4}>
      <Box
        sx={{
          height: "100%",
          minHeight: 500,
          display: "flex",
          flexDirection: "column",
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
        }}
      >
        {/* Header */}
        <Box sx={{
          py: { xs: 2, md: "23px" },
          px: { xs: 2, md: 3 },
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}>
          <Typography variant="h4">Today's Shift</Typography>
        </Box>

        {/* Content */}
        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            p: { xs: 2, md: 3 }
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              width: { xs: '80%', sm: '50%' }
            }}
          >
            {hasShiftGetPermission && (
              <>
                <Box sx={{ display: 'flex', flexDirection: 'column', mb: 1, gap: 1 }}>
                  <Typography variant="h3">
                    {totalDisplay} Hrs
                  </Typography>
                  <Typography variant="h6" color="secondary">
                    Hours Worked Today
                  </Typography>
                  <Typography variant="h6" color="secondary">
                    {dateDisplay}
                  </Typography>
                </Box>
              </>
            )}
            <Button
              variant="contained"
              color={checkIn ? "error" : "main"}
              disabled={!hasShiftSavePermission}
              {...(hasShiftSavePermission && {
                onClick: handleCheckIn
              })}
              fullWidth
              size="medium"
              sx={{ mb: 2.5 }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <Icon icon="tabler:stopwatch" />
                <Box>
                  <Typography variant="body2" color="white" sx={{ whiteSpace: 'nowrap' }}>
                    Punch {checkIn ? "Out" : "In"}
                  </Typography>
                  <Typography variant="body2" color="white">
                    {currentDisplay}
                  </Typography>
                </Box>
              </Box>
            </Button>
          </Box>
        </Box>
      </Box>           
    </Grid>

    {/* Clock In/Out Table Section */}
    <Grid item xs={12} md={8}>
        <Box sx={{ 
            height: "100%",
            minHeight: 500,
            border: '1px solid',
            borderColor: 'divider', 
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column'
        }}>
            {/* Header */}
            <Box sx={{ 
                p: { xs: 2, md: "19px" },
                borderBottom: '1px solid',
                borderColor: 'divider'
            }}>
                    <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        width: '100%' 
      }}>
        <Typography variant="h4">Punch In/Out</Typography>

        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            variant="contained"
            color="main"
            startIcon={<AddIcon />}
            disabled={!hasShiftSavePermission}
            {...(hasShiftSavePermission && {
              onClick: handleOpenAddEmpDlg
            })}
            sx={{ minWidth: 'auto' }}
          >
            Add Employee
          </Button>
        </Box>
      </Box>
            </Box>
            <Box sx={{ 
                p: { xs: 1, md: 2 },
                flex: 1,
                display: 'flex',
                flexDirection: 'column'
            }}>
                {/* Search and Filter Row */}
                <Box sx={{ 
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 2,
                    mb: 3,
                    mt: 1.5,
                }}>
                    <Box sx={{ 
                        display: "flex", 
                        gap: 2, 
                        flexWrap: "wrap",
                        flex: 1
                    }}>
                        <CustomTextField
                            value={searchText}
                            placeholder="Search"
                            onChange={(e) => handleSearchChange(e.target.value)}
                            size="small"
                            sx={{ minWidth: 200 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Icon icon="tabler:search" />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        {searchText && (
                                            <IconButton
                                                edge="end"
                                                onClick={handleClearSearch}
                                                aria-label="clear"
                                                size="small"
                                            >
                                                <ClearIcon />
                                            </IconButton>
                                        )}
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Box>

                    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                        <Box sx={{ width: { xs: '48%', md: 160 } }}>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    disableFuture
                                    slotProps={{
                                        textField: {
                                            size: "small",
                                            fullWidth: true,
                                            label: "Start Date",
                                        },
                                        field: {
                                            clearable: true,
                                        },
                                    }}
                                />
                            </LocalizationProvider>
                        </Box>

                        <Box sx={{ width: { xs: '48%', md: 160 } }}>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    disableFuture
                                    slotProps={{
                                        textField: {
                                            size: "small",
                                            fullWidth: true,
                                            label: "End Date",
                                        },
                                        field: {
                                            clearable: true,
                                        },
                                    }}
                                />
                            </LocalizationProvider>
                        </Box>
                    </Box>
                </Box>

                {/* DataGrid */}
                <Box sx={{ 
                    height: 400, 
                    width: '100%', 
                    overflowX: 'auto',
                    flex: 1
                }}>
                    <DataGridPro
                        key={collapse ? "collapsed" : "expanded"}
                        // pagination
                        // pageSizeOptions={[10, 50, { value: -1, label: "Show All" }]}
                        // paginationModel={paginationModel}
                        // onPaginationModelChange={handlePaginationModel}
                        rows={filteredRows}
                        columns={columnsEmployee}
                        disableRowSelectionOnClick
                        slots={{
                          // noRowsOverlay: NoRowsOverlay,
                          footer: () => null
                        }}
                        sx={{
                             height: 400, // Fixed height
                              minWidth: 700,
                            "& .MuiDataGrid-columnHeaderTitle": {
                                textTransform: "none",
                                fontSize: { xs: '0.75rem', md: '0.875rem' }
                            },
                            "& .MuiDataGrid-cell": {
                                fontSize: { xs: '0.75rem', md: '0.875rem' }
                            },
                        }}
                    />
                </Box>
            </Box>
        </Box>
    </Grid>
</Grid>
        </TabPanel>
                <TabPanel value="3" sx={{ width: "100%", p: { xs: 0, md: 2 }, pt: { xs: 2, md: 0 } }}>
                  <Grid container spacing={{ xs: 2, md: 5 }}>
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 2,
                          flexDirection: { xs: 'column', sm: 'row' }
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            width: { xs: '100%', sm: 'auto' }
                          }}
                        >
                          <Badge>
                            <Avatar
                              alt={
                                onboardingStatus && onboardingStatus.fullName ? onboardingStatus.fullName : "Guest"
                              }
                              src=""
                            >
                              {onboardingStatus && onboardingStatus?.fullName
                                ? getInitials(onboardingStatus?.fullName)
                                : "G"}
                            </Avatar>
                          </Badge>
                          <PopoverButton
                            taskFilterType={taskFilterType}
                            setTaskFilterType={setTaskFilterType}
                          />
                        </Box>
                        <Box
                          sx={{ 
                            display: "flex", 
                            alignItems: "center", 
                            gap: 2,
                            width: { xs: '100%', sm: 'auto' }
                          }}
                        >
                          <CustomTextField
                            value={searchText}
                            placeholder="Search"
                            onChange={(e) => handleSearchChange(e.target.value)}
                            fullWidth
                            sx={{ height: 40 }}
                            InputProps={{
                              sx: { height: 40 },
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Icon icon="tabler:search" />
                                </InputAdornment>
                              ),
                              endAdornment: (
                                <InputAdornment position="end">
                                  {searchText && (
                                    <IconButton
                                      edge="end"
                                      onClick={handleClearSearch}
                                      aria-label="clear"
                                      size="small"
                                    >
                                      <ClearIcon />
                                    </IconButton>
                                  )}
                                </InputAdornment>
                              ),
                            }}
                          />
                          <Button
                            variant="contained"
                            color="main"
                            startIcon={<AddIcon />}
                            onClick={handleOpenAssignTaskDlg}
                            sx={{
                              height: 40,
                              minWidth: 150,
                              whiteSpace: "nowrap",
                            }}
                          >
                            Assign Task
                          </Button>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <TabContext value={tabAssignTaskValue}>
                        <TabList 
                          onChange={handleAssignTaskTabChange}
                          variant="scrollable"
                          scrollButtons="auto"
                        >
                          <Tab
                            value="1"
                            label={
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1.5,
                                  width: "100%",
                                  color: theme.palette.mode === 'dark' ? 
                                    tabAssignTaskValue === "1" ? "#fff" : "inherit" :
                                    "inherit"
                                }}
                              >
                                <Box
                                  sx={{
                                    fontSize: { xs: '0.875rem', md: "1rem" },
                                    fontWeight: 500,
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  List
                                </Box>
                              </Box>
                            }
                            sx={{ 
                              alignItems: "flex-start", 
                              py: { xs: 1, md: 1.5 },
                            }}
                          />
                          <Tab
                            value="2"
                            label={
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "center",
                                  gap: 1.5,
                                  width: "100%",
                                  color: theme.palette.mode === 'dark' ? 
                                    tabAssignTaskValue === "2" ? "#fff" : "inherit" :
                                    "inherit"
                                }}
                              >
                                <Box
                                  sx={{
                                    fontSize: { xs: '0.875rem', md: "1rem" },
                                    fontWeight: 500,
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  Calendar
                                </Box>
                              </Box>
                            }
                            sx={{ 
                              py: { xs: 1, md: 1.5 },
                            }}
                          />
                        </TabList>
                        <TabPanel value="1" sx={{ p: 0, pt: 2 }}>
                          <Box sx={{ height: 420, width: '100%', overflowX: 'auto' }}>
                            <DataGridPro
                              key={collapse ? "collapsed" : "expanded"}
                              // pagination
                              // pageSizeOptions={[
                              //   10,
                              //   50,
                              //   { value: -1, label: "Show All" },
                              // ]}
                              // paginationModel={paginationModel}
                              // onPaginationModelChange={handlePaginationModel}
                              slots={{
                                // noRowsOverlay: NoRowsOverlay,
                                footer: () => null
                              }}
                              rows={filteredAssignedTasks}
                              columns={columnsAssignedTasks}
                              sx={{
                                minWidth: 800,
                                "& .MuiDataGrid-columnHeaderTitle": {
                                  textTransform: "none",
                                  fontSize: { xs: '0.75rem', md: '0.875rem' }
                                },
                                "& .MuiDataGrid-cell": {
                                  fontSize: { xs: '0.75rem', md: '0.875rem' }
                                },
                              }}
                            />
                          </Box>
                        </TabPanel>
                        <TabPanel value="2" sx={{ p: 0, pt: 2 }}>
                          <Box sx={{ height: 420, overflow: 'auto' }}>
                            <Calendar />
                          </Box>
                        </TabPanel>
                      </TabContext>
                    </Grid>
                  </Grid>
                </TabPanel>
                <TabPanel value="4" sx={{ width: "100%", p: { xs: 0, md: 2 }, pt: { xs: 2, md: 0 } }}>
                  <Grid container spacing={{ xs: 2, md: 5 }}>
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          flexDirection: { xs: 'column', sm: 'row' },
                          gap: 2
                        }}
                      >
                        <Typography variant="h3" sx={{ fontSize: { xs: '1.5rem', md: '2rem' } }}>To‑Do List</Typography>
                        <Button
                          variant="contained"
                          color="main"
                          startIcon={<AddIcon />}
                          disabled={!hasToDoSavePermission}
                          {...(hasToDoSavePermission && {
                            onClick: handleOpenToDoDlg
                          })}
                          sx={{ width: { xs: '100%', sm: 'auto' } }}
                        >
                          Add To-Do
                        </Button>
                      </Box>
                    </Grid>

                    <Grid item xs={12}>
                      <TabContext value={activeButton}>
                        <TabList
                          onChange={(e, newValue) => setActiveButton(newValue)}
                          textColor="primary"
                          indicatorColor="primary"
                          variant="scrollable"
                          scrollButtons="auto"
                        >
                          {todoTabOptions.map((tab) => (
                            <Tab
                              key={tab.value} 
                              value={tab.value}
                              label={
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1.5,
                                    width: "100%",
                                    color: theme.palette.mode === 'dark' ? 
                                      activeButton === tab.value ? "#fff" : "inherit" :
                                      "inherit"
                                  }}
                                >
                                  <Box
                                    sx={{
                                      fontSize: { xs: '0.875rem', md: "1rem" },
                                      fontWeight: 500,
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {tab.label}
                                  </Box>
                                </Box>
                              }
                              sx={{ 
                                alignItems: "flex-start", 
                                py: { xs: 1, md: 1.5 },
                              }}
                            />
                            // <Tab 
                            //   key={tab.value} 
                            //   label={tab.label} 
                            //   value={tab.value} 
                            //   sx={{ minWidth: 'auto', px: 2 }}
                            // />
                          ))}
                        </TabList>
                      </TabContext>
                    </Grid>

                    {filteredTodos && filteredTodos.length > 0 ? (
                      filteredTodos.map((todo, idx) => (
                        <Grid item xs={12} key={todo.toDoListId}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              px: { xs: 2, md: 4 },
                              py: { xs: 2, md: 4 },
                              border: "1px solid #E1E1E1",
                              borderRadius: 2,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 1,
                                pr: 2
                              }}
                            >
                              <Typography variant="h5" sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}>
                                {todo.name}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}>
                                {todo.description}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}>
                                Due:{" "}
                                {todo.dueDate
                                  ? dayjs(todo.dueDate).format("MM/DD/YYYY")
                                  : "—"}
                              </Typography>
                            </Box>

                            <Checkbox
                              checked={todo.status === "Active"}
                              onChange={(e) =>
                                handleTodoCheck(todo, e.target.checked)
                              }
                              color="success"
                              icon={
                                <CheckBoxOutlineBlankIcon sx={{ fontSize: { xs: 28, md: 38 } }} />
                              }
                              checkedIcon={<CheckBoxIcon sx={{ fontSize: { xs: 28, md: 38 } }} />}
                            />
                          </Box>
                        </Grid>
                      ))
                    ) : (
                      <Grid item xs={12}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            mx: "auto",
                            border: "1px solid #EAEAEA",
                            borderRadius: 1,
                            maxWidth: "100%",
                            height: 280,
                            mt: 8,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 2,
                              justifyContent: "center",
                              alignItems: "center",
                            }}
                          >
                            <img
                              src={AddEmployeeDoodle}
                              alt="Empty State"
                              style={{ width: "100px", height: "auto" }}
                            />
                            <Typography variant="h6">
                              No To-Do items added yet
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </TabPanel>
              </Box>
            </TabContext>
          </CardContent>
        </Card>
      </Box>
      {/* Popup Dialogs */}
      <AddEmployeeDialog
        open={openAddEmpDlg}
        handleClose={handleCloseAddEmpDlg}
        fetchTimeLog={load}
        initialData={editingLog}
      />

      {/* <EditClockInOutDialog
        open={openEditClockInOutDlg}
        handleClose={handleCloseEditClockInOutDlg}
         employee={selectedEmployee}
      /> */}
      <AddToDoDialog
        open={openToDoDlg}
        handleClose={handleCloseToDoDlg}
        fetchTodos={fetchTodos}
      />
      <AssignTaskDialog
        open={openAssignTaskDlg}
        handleClose={handleCloseAssignTaskDlg}
        tasks={rowsAssignedTasks}
        fetchAndJoin={fetchAndJoin}
      />

      <DashboardTour
        isOpen={isTourOpen}
        handleTabMainChange={handleTabMainChange}
        onRequestClose={() => handleDashboardTourClose()}
      />
      <MonthlySubscriptionDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
      />
     
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </UserLayout>
  );
};

export default Dashboard;
